import { cx } from '@emotion/css';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SizeVariants } from '../../theme/themes';
import { makeCSS } from '../../utils/makeCSS';
import { useClassName } from '../../utils/useClassName';
import { Icon } from './Icon';
import { Typography } from './Typography';
import Truncate from '../utility/Truncate';

/**
 * The Breadcrumbs component is a list of links that help visualize a files location within a hierarchical structure.
 * It allows navigation within them.
 */
export interface BreadValue {
  title: string;
  url: string;
  onClick(): void;
  type?: 'breadcrumb' | 'morebutton';
}
export interface BreadcrumbsProps {
  /**The width of its container */
  width?: number;
  /**Its size */
  size?: SizeVariants.sm | SizeVariants.md;
  /**The path that we want it to display*/
  breadcrumbs?: BreadValue[];
  /**The active item is the last in the list and in some cases(like in the reviews) we don't want it to have the active state color*/
  hideActiveState?: boolean;
  cacheKey?: string;
}

const useCSS = makeCSS(({ theme, width }: BreadcrumbsProps & { theme }) => {
  return {
    root: {
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      textAlign: 'center',
      cursor: 'default',
      textWrap: 'nowrap',
      overflow: 'visible',
      minHeight: theme.spacing[5]
    },
    clickable: {
      cursor: 'pointer',
      '&:hover': {
        '& *': {
          color: theme.color.text['02']
        }
      }
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'center',
      flexShrink: 0,
      '& >*': {
        flexShrink: 0
      }
    },
    moreItem: {
      '&:hover': {
        color: theme.color.icon['02']
      }
    },
    container: {
      width: typeof width === 'number' ? width : theme.spacing[width]
    },
    //need this one for issues with text align
    display: {
      display: 'flex'
    }
  };
});

const moreButton = {
  title: 'more',
  url: '',
  onClick: null,
  type: 'morebutton' as const
};

const cacheCrumbs: { [key: string]: BreadValue[] | undefined } = {};

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  const {
    size,
    width = 400,
    breadcrumbs,
    hideActiveState = false,
    cacheKey
  } = props;
  const lastItemColor = hideActiveState ? '03' : '01';

  const classes = useCSS(props);

  const [containerWidth, setContainerWidth] = useState(width);
  const [containerRef, setContainerRef] = useState(null);

  const [visibleBreadcrumbs, setVisibleBreadcrumbs] = useState<
    BreadValue[] | undefined
  >();
  const itemsRefs = useRef([]);
  const moreRef = useRef(null);

  // we subscribe to resize events for the container, and perform the intial measurement
  // since this is a layout effect, it happens before the visible render
  useLayoutEffect(() => {
    if (!containerRef) {
      return;
    }
    const resizeObser = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          const contentBoxSize = entry.contentBoxSize[0];
          setContainerWidth(contentBoxSize.inlineSize);
        }
      }
    });

    resizeObser.observe(containerRef);
    setContainerWidth(containerRef.inlineSize);
    return () => {
      resizeObser.disconnect();
    };
  }, [containerRef]);

  // if the breadcrumbs changes (from defaultValue `[]` to actual value), we invalidate the calculated breadcrumbs to trigger re-calculate
  useEffect(() => {
    setVisibleBreadcrumbs(undefined);
  }, [breadcrumbs]);

  // if the containerwidth changes, because of a resize, we invalidate the calculated breadcrumbs
  useLayoutEffect(() => {
    // if possible, use the cached breadcrumbs before the visible render
    if (!containerWidth && cacheCrumbs[cacheKey]) {
      setVisibleBreadcrumbs(cacheCrumbs[cacheKey]);
    } else {
      setVisibleBreadcrumbs(undefined);
    }
  }, [containerWidth, cacheKey]);

  // if the visibileBreadcrumbs calculation hasn't been done (or has been invalidated, we calculate)
  useLayoutEffect(() => {
    if (!visibleBreadcrumbs) {
      // we use the itemrefs provided by the first invisible render (see below)
      const itemWidths = itemsRefs.current.map((el) => el?.clientWidth, 0);
      const totalItemsWidth = itemWidths.reduce((sum, w) => sum + w, 0);
      const moreItemWidth = moreRef.current?.clientWidth;

      // first we check if all the breadcrumbs would fit and stop any further calculations if that's the case
      if (totalItemsWidth < containerWidth) {
        setVisibleBreadcrumbs(breadcrumbs);
        return;
      }

      // if we cannot show even 1 breadcrumb + the more button, we only show the last breadcrumb
      const twoItemsWidth = itemWidths[0] + moreItemWidth;
      if (twoItemsWidth > containerWidth) {
        setVisibleBreadcrumbs([breadcrumbs[breadcrumbs.length - 1]]);
        return;
      }

      // if we can replace the second item with the "more button" and render the rest of the breadcrumbs normally, we do that
      // NOTE: this logic could be expanded to check if replacing any of the breadcrumbs with the "more button" would make them fit
      const itemsWithMoreWidth =
        totalItemsWidth - itemsRefs.current[1]?.clientWidth + moreItemWidth;

      if (itemsWithMoreWidth < containerWidth) {
        setVisibleBreadcrumbs([
          breadcrumbs[0],
          moreButton,
          ...breadcrumbs.slice(2)
        ]);
        return;
      }

      // If all else fail we add the breadcrumbs 1 by 1 until there's no more room
      // Because we know we can't fit all the crumbs, we can always replace the second crumb with the "more button"
      //
      // two rules:
      // - we always want to show the first crumb + the "more button"
      // - we want to start adding the rest of the breadcrumbs starting from the last one
      //
      //
      const visible = [...Array(breadcrumbs.length)]
        // for that second part we build an array that contains the index of the breadcrumbs in the order we want to add them
        // e.g.  `[0,1,5,4,3,2]`
        .map((i, idx, arr) => {
          if (idx < 2) {
            return idx;
          }
          return arr.length - (idx - 1);
        })
        // now we try adding the breadcrumbs
        .reduce(
          (acc, idx) => {
            // width of the current breadcrumb
            const w = idx === 1 ? moreItemWidth : itemWidths[idx];

            // if adding the breadcrumb would fit, we add it
            if (acc.w + w < containerWidth) {
              acc.w += w;

              // first two breadcrumbs get pushed to the end of the array
              if (idx < 2) {
                acc.c = [...acc.c, idx === 1 ? moreButton : breadcrumbs[idx]];
                // other breadcrumbs get inserted between the first two and the rest
              } else {
                acc.c = [
                  ...acc.c.slice(0, 2),
                  breadcrumbs[idx],
                  ...acc.c.slice(2)
                ];
              }
            }

            return acc;
          },
          { w: 0, c: [] }
        );

      /**
       * if it displays only two items and the second one it the our more item
       * display only the location of the asset
       */
      if (visible.c?.length === 2) {
        if (visible.c[1].type === 'morebutton')
          setVisibleBreadcrumbs([breadcrumbs[breadcrumbs.length - 1]]);
      } else {
        setVisibleBreadcrumbs(visible.c);
      }
    }
  }, [visibleBreadcrumbs, breadcrumbs, containerWidth]);

  //cache visiblebreadcrumbs
  useEffect(() => {
    return () => {
      if (visibleBreadcrumbs?.length) {
        cacheCrumbs[cacheKey] = visibleBreadcrumbs;
      }
    };
  }, [visibleBreadcrumbs, cacheKey]);

  // if we haven't calculated yet (or calculation has been cleared because of a resize)
  // we render all the breadcrumbs + the "more" breadcrumb so we can measure them
  // since we use layoutEffects, this part shouldn't be visibile to the user
  if (!visibleBreadcrumbs) {
    return (
      <div
        className={useClassName(width && classes.container)}
        ref={setContainerRef}
      >
        <div className={`${classes.root}`}>
          {breadcrumbs.map((item, index, arr) => {
            const theLast = index === arr.length - 1;

            return (
              <>
                {theLast ? (
                  <div
                    className={`${classes.listItem}`}
                    ref={(el) => (itemsRefs.current[index] = el)}
                    key={`${item.title}-${item.url}`}
                    onClick={item.onClick}
                  >
                    <span
                      className={cx(
                        hideActiveState ? classes.clickable : '',
                        classes.display
                      )}
                    >
                      <Typography variant={size} colorVariant={lastItemColor}>
                        {item.title}
                      </Typography>
                    </span>
                  </div>
                ) : (
                  <div
                    ref={(el) => (itemsRefs.current[index] = el)}
                    key={`${item.title}-${item.url}`}
                    className={`${classes.listItem}`}
                    onClick={item.onClick}
                  >
                    <span className={`${classes.clickable} ${classes.display}`}>
                      <Typography variant={size} colorVariant="03">
                        {item.title}
                      </Typography>
                    </span>

                    <Icon icon="chevron_right" colorVariant="03" size={size} />
                  </div>
                )}

                <div className={`${classes.listItem}`} ref={moreRef}>
                  {/**The dot/more item */}
                  <span className={`${classes.moreItem} ${classes.display} `}>
                    <Icon icon="more" colorVariant="03" size={size} />
                  </span>
                  <Icon icon="chevron_right" colorVariant="03" size={size} />
                </div>
              </>
            );
          })}
        </div>
      </div>
    );
  }
  // Truncate the text if there's only one breadcrumb and has long text
  if (visibleBreadcrumbs?.length === 1) {
    return (
      <div
        className={useClassName(width && classes.container)}
        ref={setContainerRef}
      >
        <div
          className={`${classes.listItem}`}
          ref={(el) => (itemsRefs.current[0] = el)}
          key={`${visibleBreadcrumbs[0].title}-${visibleBreadcrumbs[0].url}`}
        >
          <span
            className={cx(
              hideActiveState ? classes.clickable : '',
              classes.display
            )}
            onClick={visibleBreadcrumbs[0].onClick}
          >
            <Typography variant={size} colorVariant={lastItemColor}>
              <Truncate
                str={visibleBreadcrumbs[0].title}
                widthLimit={containerWidth}
              />
            </Typography>
          </span>
        </div>
      </div>
    );
  }

  // render the actual "visibleBreadcrumbs" we've calculated
  return (
    <div
      className={useClassName(width && classes.container)}
      ref={setContainerRef}
    >
      <div className={`${classes.root}`}>
        {visibleBreadcrumbs.map((item, index) => {
          const theLast = index === visibleBreadcrumbs.length - 1;

          if (item.type === 'morebutton') {
            return (
              <div
                className={`${classes.listItem}`}
                ref={moreRef}
                key={`${item.title}-${item.url}`}
              >
                {/**The dot/more item */}
                <span className={`${classes.moreItem} ${classes.display} `}>
                  <Icon icon="more" colorVariant="03" size={size} />
                </span>
                {!theLast && (
                  <Icon icon="chevron_right" colorVariant="03" size={size} />
                )}
              </div>
            );
          }

          return (
            <>
              {theLast ? (
                <div
                  className={`${classes.listItem}`}
                  ref={(el) => (itemsRefs.current[index] = el)}
                  key={`${item.title}-${item.url}`}
                >
                  <span
                    className={cx(
                      hideActiveState ? classes.clickable : '',
                      classes.display
                    )}
                    onClick={item.onClick}
                  >
                    <Typography variant={size} colorVariant={lastItemColor}>
                      {item.title}
                    </Typography>
                  </span>
                </div>
              ) : (
                <div
                  ref={(el) => (itemsRefs.current[index] = el)}
                  key={`${item.title}-${item.url}`}
                  className={`${classes.listItem}`}
                >
                  <span
                    className={`${classes.clickable} ${classes.display}`}
                    onClick={item.onClick}
                  >
                    <Typography variant={size} colorVariant="03">
                      {item.title}
                    </Typography>
                  </span>

                  <Icon icon="chevron_right" colorVariant="03" size={size} />
                </div>
              )}
            </>
          );
        })}
      </div>
    </div>
  );
};
